// Scope-neutral duration statistics. A "duration observation" is one measurement — a whole
// process instance, or one occurrence of one step — so the overview and the step inspector share
// both these numbers and the chart code that draws them.
const DURATION_HISTOGRAM_BIN_COUNT = 17;

// A relative spread of 0.5 means the standard deviation is at least half the mean. Unlike raw
// variance, this remains comparable when one process takes seconds and another takes hours.
const HIGH_DURATION_VARIABILITY_CV = 0.5;
const MIN_DURATION_VARIABILITY_OBSERVATIONS = 3;

export type DurationObservation = {
  id: string;
  duration: number;
  date?: string;
  timestamp?: string;
};

export const median = (sorted: number[]): number => {
  const n = sorted.length;
  if (n === 0) return 0;
  const mid = n >> 1;
  return n % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** The value `p` of the way through `sorted`, interpolating between the two nearest. */
export const quantile = (sorted: number[], p: number): number => {
  if (sorted.length === 0) return 0;
  const position = (sorted.length - 1) * p;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
};

/** Scale-independent duration spread. Returns zero when no meaningful positive mean exists. */
export const durationCoefficientOfVariation = (values: number[]): number => {
  const valid = values.filter(value => Number.isFinite(value) && value >= 0);
  if (valid.length === 0) return 0;
  const mean = valid.reduce((sum, value) => sum + value, 0) / valid.length;
  if (mean <= 0) return 0;
  const variance = valid.reduce((sum, value) => sum + (value - mean) ** 2, 0) / valid.length;
  return Math.sqrt(variance) / mean;
};

export const hasHighDurationVariability = (values: number[]): boolean => {
  const valid = values.filter(value => Number.isFinite(value) && value >= 0);
  return (
    valid.length >= MIN_DURATION_VARIABILITY_OBSERVATIONS &&
    durationCoefficientOfVariation(valid) >= HIGH_DURATION_VARIABILITY_CV
  );
};

type DurationAnalytics = {
  durations: number[];
  trend: Array<DurationObservation & { date: string }>;
  min: number;
  median: number;
  max: number;
};

/**
 * Scope-neutral duration statistics used by both process instances and step occurrences.
 * Observations without a date still contribute to the distribution, but not to the time series,
 * which has nowhere to put them.
 */
export const durationAnalytics = (observations: DurationObservation[]): DurationAnalytics => {
  const valid = observations.filter(observation => Number.isFinite(observation.duration));
  const durations = valid.map(observation => observation.duration).sort((a, b) => a - b);
  const trend = valid
    .flatMap(observation => (observation.date ? [{ ...observation, date: observation.date }] : []))
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) || (a.timestamp ?? "").localeCompare(b.timestamp ?? ""),
    );

  return {
    durations,
    trend,
    min: durations[0] ?? 0,
    median: median(durations),
    max: durations[durations.length - 1] ?? 0,
  };
};

type DurationHistogramBin = {
  min: number;
  max: number;
  count: number;
};

// A selected duration range can supply its own domain. Zooming asks for more bars over the same
// domain, so zooming reads finer without moving the filter boundaries.
export const durationHistogramBins = (
  values: number[],
  binCount = DURATION_HISTOGRAM_BIN_COUNT,
  domain?: { min: number; max: number },
): DurationHistogramBin[] => {
  if (values.length === 0) return [];
  const valid = values.filter(value => Number.isFinite(value) && value >= 0);
  if (valid.length === 0) return [];
  const domainMin = domain?.min ?? 0;
  const domainMax = domain?.max ?? Math.max(1, Math.ceil(Math.max(...valid)));
  const binDuration = (domainMax - domainMin) / binCount;
  const bins = Array.from({ length: binCount }, (_, index) => ({
    min: domainMin + index * binDuration,
    max: domainMin + (index + 1) * binDuration,
    count: 0,
  }));

  for (const value of valid) {
    if (value < domainMin || value > domainMax) continue;
    const index = Math.min(binCount - 1, Math.floor((value - domainMin) / binDuration));
    bins[index].count += 1;
  }

  return bins;
};

export type TrendGrouping = "day" | "week" | "month";

const DAY_MS = 24 * 60 * 60 * 1000;

/** A `YYYY-MM-DD` date as UTC midnight, so period arithmetic never meets a DST shift. */
const dayMs = (date: string) => Date.parse(`${date}T00:00:00Z`);
const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** The first day of the period `date` falls in: itself, its week's Monday, or its month's 1st. */
const periodStart = (date: string, grouping: TrendGrouping): string => {
  if (grouping === "day") return date;
  if (grouping === "month") return `${date.slice(0, 7)}-01`;
  const ms = dayMs(date);
  return isoDay(ms - ((new Date(ms).getUTCDay() + 6) % 7) * DAY_MS);
};

const nextPeriod = (start: string, grouping: TrendGrouping): string => {
  if (grouping === "day") return isoDay(dayMs(start) + DAY_MS);
  if (grouping === "week") return isoDay(dayMs(start) + 7 * DAY_MS);
  const [year, month] = start.split("-").map(Number);
  return isoDay(Date.UTC(year, month, 1));
};

/**
 * The finest grouping that still reads as a trend: days across a few weeks, weeks across a few
 * months, months beyond that. Past those spans a day or week point stands for too few cases.
 */
export const defaultTrendGrouping = (dates: string[]): TrendGrouping => {
  if (dates.length === 0) return "day";
  const days = (dayMs(dates[dates.length - 1]) - dayMs(dates[0])) / DAY_MS;
  return days <= 21 ? "day" : days <= 120 ? "week" : "month";
};

export type TrendPeriod = {
  /** First day of the period, `YYYY-MM-DD`. */
  start: string;
  /** Null for a period with no observations, kept so the gap shows on the axis. */
  stats: { count: number; median: number } | null;
};

/** Every period from the first observation's to the last one's, each with its count and median. */
export const durationTrend = (
  points: Array<{ date: string; duration: number }>,
  grouping: TrendGrouping,
  referencePoints = points,
): TrendPeriod[] => {
  const group = (observations: typeof points) => {
    const grouped = new Map<string, number[]>();
    for (const point of observations) {
      const start = periodStart(point.date, grouping);
      const durations = grouped.get(start);
      if (durations) durations.push(point.duration);
      else grouped.set(start, [point.duration]);
    }
    for (const durations of grouped.values()) durations.sort((a, b) => a - b);
    return grouped;
  };
  const byPeriod = group(points);
  const reference = referencePoints === points ? byPeriod : group(referencePoints);
  const starts = [...new Set([...byPeriod.keys(), ...reference.keys()])].sort();
  const periods: TrendPeriod[] = [];
  for (let start = starts[0]; start && start <= starts[starts.length - 1]; ) {
    const durations = byPeriod.get(start);
    periods.push({
      start,
      stats: durations
        ? {
            count: durations.length,
            median: median(durations),
          }
        : null,
    });
    start = nextPeriod(start, grouping);
  }
  return periods;
};
